import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useContext,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { DataRoomBuySideGroup } from 'router/subrouters/DataRoom/models/DataRoomMember';
import { Button, Collapsible } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import ChevrondownIcon from 'icons/Chevrondown.icon';
import MembersTable from '../../MembersTable';
import {
  getArrayLength,
  getBuySideGroupAnchorId,
  getIsNewMember,
  getShowGroupChat,
  getUserFullName,
} from 'utils';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import MembersContext from 'router/subrouters/DataRoom/providers/Members/Members.context';
import SettingsIcon from 'icons/Settings.icon';
import ChatIcon from 'icons/Chat.icon';
import ManageBuyerGroup from '../../ManageBuyerGroup';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { CollapsibleRef } from 'ncoded-component-library/build/components/molecules/Collapsible/Collapsible.component';
import { NewTag } from 'router/subrouters/DataRoom/components/NewTag';
import Counter from 'components/Counter';
import { COUNTER_VARIANTS } from 'components/Counter/Counter.component';
import useGroupChatSellSideDiscovery from '../../../hooks/useGroupChatSellSideDiscovery';

import './BuySideInstance.styles.scss';

type BuySideInstanceProps = {
  className?: string;
  group: DataRoomBuySideGroup;
  showEditGroupButton?: boolean;
};

const BuySideInstance: ForwardRefRenderFunction<
  CollapsibleRef,
  BuySideInstanceProps
> = (props, ref) => {
  const { className, group, showEditGroupButton = true } = props;

  const {
    name,
    activeChatSessionId,
    closedChatSessions = [],
    dataRoomMembers,
  } = group;

  const { doesntHaveAccess, openChatDrawer } = useContext(DataRoomContext);
  const { highlightedBuySideGroupId } = useContext(MembersContext);

  const { shouldHighlightGroupChatButton, markDrawerOpened } =
    useGroupChatSellSideDiscovery();

  const [collapsible, collapsibleRef] = useCallbackRef<CollapsibleRef>(null);
  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  useImperativeHandle(ref, () => collapsible, [collapsible]);

  const lead = useMemo(
    () => dataRoomMembers.find(({ isLead }) => isLead),
    [dataRoomMembers],
  );

  const showGroupChat = useMemo(
    () => getShowGroupChat(activeChatSessionId, dataRoomMembers),
    [dataRoomMembers, activeChatSessionId],
  );

  const hasNewMember = useMemo(
    () => !!dataRoomMembers.find(getIsNewMember),
    [dataRoomMembers],
  );

  const previousGroupChats = useMemo(
    () => ({
      activeChatSessionId,
      closedChatSessions,
    }),
    [activeChatSessionId, closedChatSessions],
  );

  const dataRoomMembersCount = getArrayLength(dataRoomMembers);

  const showLeadTag = useMemo(
    () => !!lead && dataRoomMembersCount > 1,
    [dataRoomMembersCount, lead],
  );

  const classes = classNames(
    'buy-side-instance',
    {
      pulsate: highlightedBuySideGroupId === group._id,
    },
    className,
  );

  const { t } = useTranslation();

  return (
    <div className={classes} id={getBuySideGroupAnchorId(group._id)}>
      <Collapsible
        ref={collapsibleRef}
        defaultOpen={hasNewMember}
        trigger={
          <div className="trigger">
            <div className="trigger__title">
              <p className="name">{name}</p>
              <Counter
                count={dataRoomMembersCount}
                variant={COUNTER_VARIANTS.GREY}
              />
              {showLeadTag && (
                <p className="lead">
                  {getUserFullName(lead.firstName, lead.lastName)}
                </p>
              )}
              {hasNewMember && <NewTag variant="green" time={new Date()} />}
            </div>

            <div className="trigger__actions">
              {showGroupChat && (
                <Button
                  variant="outline"
                  icon={ChatIcon}
                  className={classNames({
                    'new-feature': shouldHighlightGroupChatButton,
                  })}
                  onClick={(e) => {
                    e.stopPropagation();
                    markDrawerOpened();
                    openChatDrawer(
                      activeChatSessionId,
                      activeChatSessionId,
                      previousGroupChats,
                    );
                  }}
                >
                  {t('groupChat')}
                </Button>
              )}

              {!doesntHaveAccess && showEditGroupButton && (
                <Button
                  icon={SettingsIcon}
                  onClick={(e) => {
                    e.stopPropagation();
                    drawer?.setOpen(true);
                  }}
                />
              )}
              <ChevrondownIcon />
            </div>
          </div>
        }
      >
        <MembersTable
          isBuySide
          members={dataRoomMembers}
          previousGroupChats={previousGroupChats}
        />
      </Collapsible>
      <ManageBuyerGroup ref={drawerRef} group={group} />
    </div>
  );
};

export default forwardRef(BuySideInstance);
